import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { FormattedMessage } from 'react-intl';

import { OptionFormGroup } from '@/App/tiles/Option/fields/OptionFormGroup.tsx';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { DatePickerWithTenorNext } from '@/components/ui/DatePickerWithTenorNext.tsx';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import { selectOptionLegsProperties, selectOptionLegsValidation } from '@/store/option/selectors/optionSelectors.ts';
import { type LegId, premiumTenors } from '@/store/option/state.ts';

type PremiumDateProps = {
  tileId: string;
};

export function PremiumDate({ tileId }: PremiumDateProps) {
  const [checkOption] = useCheckOption();
  const legId: LegId = `${tileId}/0`;

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const { premiumPaymentDate, premiumPaymentDateTenor, validation } = useOptionSelector((tile) => {
    const tradeCapture = tile.tradeCapture;
    const leg = selectOptionLegsProperties(tile, legId);
    const validation = selectOptionLegsValidation(tile, legId)?.premiumPaymentDate;

    return {
      premiumPaymentDate: leg?.premiumPaymentDate,
      premiumPaymentDateTenor: leg?.premiumPaymentDateTenor,
      optionTileProductName: tile.type,
      tradeCapture,
      validation,
    };
  });

  const handlePremiumPaymentDateSelected = (dateOrTenor: string) => {
    sgwtWebAnalytics?.trackEvent('Option', 'Premium payment date selected', dateOrTenor);

    checkOption({
      tileId,
      properties: {
        premiumPaymentDate: dateOrTenor,
      },
    });
  };

  return (
    <OptionFormGroup label={<FormattedMessage id="option.fields.premiumDate" />}>
      <TradeCaptureValidationWrapper validation={validation}>
        <DatePickerWithTenorNext
          data-e2e="premium-payment-date"
          date={premiumPaymentDate}
          tenor={premiumPaymentDateTenor}
          allTenors={premiumTenors}
          onChange={handlePremiumPaymentDateSelected}
        />
      </TradeCaptureValidationWrapper>
    </OptionFormGroup>
  );
}
